<template>
  <div class="dashboard-container">
    <div class="dashboard-content">
      <div class="dashboard-header">
        <h1 class="dashboard-title">{{ $t('dashboard.title') }}</h1>
        <BaseButton 
          variant="primary" 
          size="sm" 
          class="airdrop-button"
          @click="goToAirdrop"
        >
          
          Airdrop
          <img 
            src="../assets/hype-logo.png"
            alt="HYPE"
            class="airdrop-token-icon"
            @error="handleIconError"
          />
        </BaseButton>
      </div>

      <!-- Period Selector -->
      <div class="period-selector">
        <button
          v-for="period in periods"
          :key="period.value"
          class="period-button"
          :class="{ active: selectedPeriod === period.value }"
          @click="selectedPeriod = period.value"
        >
          {{ period.label }}
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loader-dots">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
        <p class="loading-text">{{ $t('dashboard.loadingData') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <p class="error-title">{{ $t('dashboard.unableToLoad') }}</p>
        <p class="error-message">{{ error }}</p>
        <BaseButton variant="outline" @click="loadDashboardData">{{ $t('dashboard.tryAgain') }}</BaseButton>
      </div>

      <!-- Dashboard Content -->
      <div v-else class="dashboard-sections">
        <!-- Main Statistics -->
        <BaseCard class="stats-container">
          <div class="stats-content">
            <!-- Main Balance - крупно и выделено -->
            <div class="stats-hero">
              <div class="stats-balance">
                <span class="stats-balance-label">{{ $t('dashboard.totalBalance') }}</span>
                <div class="stats-balance-value">
                  {{ formatBalance(currentBalance) }} USDT
                </div>
              </div>
            </div>
            
            <!-- Метрики в общем гриде -->
            <div class="stats-metrics-grid">
              <!-- Unrealized PnL -->
              <div class="stats-metric-card">
                <span class="stats-metric-label">{{ $t('dashboard.unrealizedPnl') }}</span>
                <div class="stats-metric-value" :class="getProfitClass(unrealizedPnl)">
                  <span class="stats-icon" :class="getProfitClass(unrealizedPnl)">
                    {{ unrealizedPnl >= 0 ? '▲' : '▼' }}
                  </span>
                  {{ formatProfit(unrealizedPnl) }}
                </div>
              </div>

              <!-- Realized PnL -->
              <div class="stats-metric-card">
                <span class="stats-metric-label">{{ $t('dashboard.realizedPnl') }}</span>
                <div class="stats-metric-value" :class="getProfitClass(currentPeriodProfit)">
                  <span class="stats-icon" :class="getProfitClass(currentPeriodProfit)">
                    {{ currentPeriodProfit >= 0 ? '▲' : '▼' }}
                  </span>
                  {{ formatProfit(currentPeriodProfit) }}
                </div>
              </div>

              <!-- Total ROI -->
              <div class="stats-metric-card">
                <span class="stats-metric-label">{{ $t('dashboard.totalRoi') }}</span>
                <div class="stats-metric-value" :class="getProfitClass(roi)">
                  <span class="stats-icon" :class="getProfitClass(roi)">
                    {{ roi >= 0 ? '▲' : '▼' }}
                  </span>
                  {{ formatROI(roi) }}
                </div>
              </div>

              <!-- Open / Closed -->
              <div class="stats-metric-card positions-card">
                <span class="stats-metric-label">{{ $t('dashboard.positions') }}</span>
                <div class="positions-stats">
                  <div class="position-stat-item">
                    <div class="position-stat-icon open-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5" fill="none"/>
                        <path d="M8 4V8L10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </div>
                    <div class="position-stat-content">
                      <span class="position-stat-label">{{ $t('dashboard.open') }}</span>
                      <span class="position-stat-value">{{ openPositionsCount }}</span>
                    </div>
                  </div>
                  <div class="position-stat-divider"></div>
                  <div class="position-stat-item">
                    <div class="position-stat-icon closed-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </div>
                    <div class="position-stat-content">
                      <span class="position-stat-label">{{ $t('dashboard.closed') }}</span>
                      <span class="position-stat-value">{{ closedPositionsCount }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Win Rate -->
              <div class="stats-metric-card">
                <span class="stats-metric-label">{{ $t('dashboard.winRate') }}</span>
                <span
                  class="stats-metric-value"
                  :class="winRate >= 50 ? 'profit-positive' : 'profit-negative'"
                >
                  {{ winRate.toFixed(1) }}%
                </span>
              </div>

              <!-- Avg PnL & Volume -->
              <div class="stats-metric-card">
                <span class="stats-metric-label">{{ $t('dashboard.avgPnlVolume') }}</span>
                <div class="stats-metric-subvalues">
                  <span class="stats-metric-subvalue">
                    <span class="stats-secondary-label">{{ $t('dashboard.avgPnl') }}</span>
                    <span
                      class="stats-secondary-value"
                      :class="getProfitClass(averagePnL)"
                    >
                      {{ formatProfit(averagePnL) }}
                    </span>
                  </span>
                  <span class="stats-metric-subvalue">
                    <span class="stats-secondary-label">{{ $t('dashboard.volume') }}</span>
                    <span class="stats-secondary-value">
                      {{ formatBalance(totalVolume) }}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </BaseCard>

        <!-- Profitability Chart -->
        <BaseCard class="chart-card">
          <h2 class="section-title">{{ $t('dashboard.profitabilityChart') }}</h2>
          <div class="chart-container" @mouseleave="hideTooltip">
            <canvas 
              ref="chartCanvas" 
              class="chart-canvas"
              @mousemove="handleChartHover"
              @touchmove="handleChartTouch"
              @touchstart="handleChartTouch"
              @touchend="hideTooltip"
            ></canvas>
            <div 
              v-if="tooltip.visible" 
              class="chart-tooltip"
              :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
            >
              <div class="tooltip-date">{{ tooltip.date }}</div>
              <div class="tooltip-value">{{ tooltip.value }}</div>
            </div>
          </div>
        </BaseCard>

        <!-- Open Positions -->
        <div class="positions-section">
          <div class="positions-header">
            <h2 class="section-title">{{ $t('dashboard.openPositions') }}</h2>
            <BaseButton 
              variant="outline" 
              size="sm" 
              @click="goToPositionsHistory"
              class="history-button"
            >
              {{ $t('dashboard.positionsHistory') }}
            </BaseButton>
          </div>
          <div v-if="openedPositions.length === 0" class="empty-positions">
            <p class="empty-text">{{ $t('dashboard.noOpenPositions') }}</p>
          </div>
          <div v-else class="positions-list">
            <BaseCard
              v-for="(position, index) in openedPositions"
              :key="position.position_id"
              class="position-card"
            >
              <div class="position-header">
                <div class="position-pair">
                  <span 
                    class="position-coin"
                    :class="{ clickable: position.source !== 'self' }"
                    @click="onCoinClick(position)"
                  >
                    {{ (position.source !== 'self' || !position.coin) ? '*****' : position.coin }}/USDT
                  </span>
                  <span class="position-direction" :class="position.direction">
                    {{ position.direction === 'long' ? '↑' : '↓' }} {{ position.direction.toUpperCase() }}
                  </span>
                </div>
                <span class="cross-margin-label">{{ $t('dashboard.crossMargin') }}</span>
              </div>
              <div class="position-details">
                <div class="position-detail-row">
                  <span class="detail-label">{{ $t('dashboard.leverage') }}</span>
                  <span class="detail-value">x{{ position.leverage || userLeverage }}</span>
                </div>
                <div class="position-detail-row">
                  <span class="detail-label">{{ $t('dashboard.margin') }}</span>
                  <span class="detail-value">{{ formatMargin(position.margin) }} USDT</span>
                </div>
                <div class="position-detail-row highlight-row" v-if="getPositionPnlUsdt(position, index) !== null">
                  <span class="detail-label">{{ $t('dashboard.pnlUnrealized') }}</span>
                  <span class="detail-value pnl-value" :class="getPnlClass(getPositionPnlUsdt(position, index)?.usdt || getPositionPnlUsdt(position, index))">
                    <span class="pnl-icon" :class="getPnlClass(getPositionPnlUsdt(position, index)?.usdt || getPositionPnlUsdt(position, index))">
                      {{ (getPositionPnlUsdt(position, index)?.usdt || getPositionPnlUsdt(position, index)) >= 0 ? '▲' : '▼' }}
                    </span>
                    {{ formatPnlUsdt(getPositionPnlUsdt(position, index)) }}
                  </span>
                </div>
              </div>
              <!-- DEBUG PANEL -->
              <div class="debug-panel" v-if="showDebugPanel">
                <div class="debug-header">
                  <span class="debug-title">🔧 DEBUG</span>
                  <span class="debug-source" :class="getDebugSource(position).type">
                    {{ getDebugSource(position).label }}
                  </span>
                </div>
                <div class="debug-content">
                  <div class="debug-row">
                    <span class="debug-label">Coin ID:</span>
                    <span class="debug-value">{{ position.coin_id || '—' }}</span>
                  </div>
                  <div class="debug-row">
                    <span class="debug-label">Entry Price:</span>
                    <span class="debug-value">{{ getDebugEntryPrice(position) }}</span>
                  </div>
                  <div class="debug-row">
                    <span class="debug-label">Current Price:</span>
                    <span class="debug-value">{{ getDebugCurrentPrice(position) }}</span>
                  </div>
                  <div class="debug-row">
                    <span class="debug-label">Date Opened:</span>
                    <span class="debug-value">{{ formatDebugDate(position.date_opened) }}</span>
                  </div>
                  <div class="debug-row" v-if="realPrices[position.position_id || position.id]">
                    <span class="debug-label">Last Updated:</span>
                    <span class="debug-value">{{ formatDebugTime(realPrices[position.position_id || position.id]?.lastUpdated) }}</span>
                  </div>
                </div>
              </div>
            </BaseCard>
          </div>
        </div>
      </div>
    </div>

    <BottomMenu />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import BaseCard from '../components/ui/BaseCard.vue'
import BaseButton from '../components/ui/BaseButton.vue'
import BottomMenu from '../components/ui/BottomMenu.vue'
import { apiService } from '../services/api.js'
import { useTelegramData } from '../composables/useTelegram.js'
import { useProfileData } from '../composables/useProfileData.js'
import { useTradePnl } from '../composables/useTradePnl.js'

export default {
  name: 'DashboardScreen',
  components: {
    BaseCard,
    BaseButton,
    BottomMenu
  },
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const { userData } = useTelegramData()
    const { profileData } = useProfileData()
    // Единый источник PnL (тот же, что на вкладке Trade)
    const { totalUnrealizedPnl, pnlById, customPnls, start: startPnl, stop: stopPnl } = useTradePnl()
    const chartCanvas = ref(null)
    
    const selectedPeriod = ref('all-time')
    const isLoading = ref(false)
    const error = ref(null)
    const openedPositions = ref([])
    const closedPositions = ref([])
    const pnlData = ref(null)
    const currentPnlIndex = ref(7) // Start with pnl7
    const realPrices = ref({}) // { positionId: { currentPrice, entryPrice, ... } } (debug)
    const showDebugPanel = ref(false) // Включить/выключить отладочную панель
    let pnlUpdateInterval = null
    
    const tooltip = ref({
      visible: false,
      x: 0,
      y: 0,
      date: '',
      value: ''
    })
    
    const currentChartData = ref([])
    const chartInitialHeight = ref(null)
    
    const periods = computed(() => [
      { label: t('dashboard.periods.today'), value: 'today' },
      { label: t('dashboard.periods.week'), value: 'week' },
      { label: t('dashboard.periods.month'), value: 'month' },
      { label: t('dashboard.periods.allTime'), value: 'all-time' }
    ])

    // Calculate leverage based on user level
    const userLeverage = computed(() => {
      const exp = profileData.value.exp ?? 0
      if (exp >= 1000) return 100 // VIP
      if (exp >= 625) return 50   // Platinum
      if (exp >= 250) return 10   // Gold
      if (exp >= 100) return 5    // Bronze
      return 3                     // Standard
    })

    // Current balance
    const currentBalance = computed(() => profileData.value.balance ?? 0)

    // Calculate profits for different periods
    const profitDay = computed(() => calculateProfitForPeriod(1))
    const profitWeek = computed(() => calculateProfitForPeriod(7))
    const profitMonth = computed(() => calculateProfitForPeriod(30))
    const profitAllTime = computed(() => calculateProfitForPeriod(null))

    // Current period profit based on selected period - используем ту же логику что и для averagePnL
    const currentPeriodProfit = computed(() => {
      const filtered = currentPeriodClosedPositions.value
      if (!filtered || filtered.length === 0) return 0
      
      const totalPnL = filtered.reduce((sum, pos) => {
        return sum + (Number(pos.realized_pnl) || 0)
      }, 0)
      
      return totalPnL
    })

    // Нереализованный PnL — единое значение из общего composable (как на Trade)
    const unrealizedPnl = computed(() => totalUnrealizedPnl.value)

    // Calculate ROI for selected period
    // ROI = (Прибыль − Вложения) / Вложения × 100%
    // Вложения = Текущий баланс - Прибыль за весь период
    const roi = computed(() => {
      const periodProfit = currentPeriodProfit.value
      const totalProfit = profitAllTime.value
      const initialInvestment = currentBalance.value - totalProfit
      
      if (initialInvestment <= 0) return 0
      
      // Для All Time используем общую формулу
      if (selectedPeriod.value === 'all-time') {
        return (totalProfit / initialInvestment) * 100
      }
      
      // Для других периодов показываем ROI за период
      return (periodProfit / initialInvestment) * 100
    })

    // Фильтрация позиций по периоду
    const getFilteredPositions = (positions, days) => {
      if (!positions || positions.length === 0) return []
      if (days === null) return positions // All time
      
      const now = new Date()
      const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
      
      return positions.filter(pos => {
        const dateStr = pos.date_closed || pos.closedAt || pos.date_opened || pos.createdAt
        if (!dateStr) return false
        
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return false
        
        return date >= cutoffDate && date <= now
      })
    }

    // Текущие отфильтрованные позиции по периоду
    const currentPeriodClosedPositions = computed(() => {
      let days = null
      switch (selectedPeriod.value) {
        case 'today': days = 1; break
        case 'week': days = 7; break
        case 'month': days = 30; break
        case 'all-time': days = null; break
      }
      return getFilteredPositions(closedPositions.value, days)
    })

    const currentPeriodOpenedPositions = computed(() => {
      let days = null
      switch (selectedPeriod.value) {
        case 'today': days = 1; break
        case 'week': days = 7; break
        case 'month': days = 30; break
        case 'all-time': days = null; break
      }
      return getFilteredPositions(openedPositions.value, days)
    })

    // Additional statistics - ВСЕ по выбранному периоду
    const openPositionsCount = computed(() => {
      if (selectedPeriod.value === 'all-time') {
        return openedPositions.value?.length || 0
      }
      return currentPeriodOpenedPositions.value?.length || 0
    })
    
    const closedPositionsCount = computed(() => currentPeriodClosedPositions.value?.length || 0)
    
    // Win Rate - процент прибыльных позиций за период
    const winRate = computed(() => {
      const filtered = currentPeriodClosedPositions.value
      if (!filtered || filtered.length === 0) return 0
      const profitableCount = filtered.filter(pos => {
        const pnl = Number(pos.realized_pnl) || 0
        return pnl > 0
      }).length
      return (profitableCount / filtered.length) * 100
    })
    
    // Average PnL per position за период
    const averagePnL = computed(() => {
      const filtered = currentPeriodClosedPositions.value
      if (!filtered || filtered.length === 0) return 0
      const totalPnL = filtered.reduce((sum, pos) => {
        return sum + (Number(pos.realized_pnl) || 0)
      }, 0)
      return totalPnL / filtered.length
    })
    
    // Total trading volume за период
    const totalVolume = computed(() => {
      const openVolume = (selectedPeriod.value === 'all-time' ? openedPositions.value : currentPeriodOpenedPositions.value).reduce((sum, pos) => {
        return sum + (Number(pos.margin) || 0)
      }, 0)
      const closedVolume = currentPeriodClosedPositions.value.reduce((sum, pos) => {
        return sum + (Number(pos.margin) || 0)
      }, 0)
      return openVolume + closedVolume
    })

    // Calculate profit for period
    function calculateProfitForPeriod(days) {
      if (!closedPositions.value || closedPositions.value.length === 0) {
        console.log('[Dashboard] No closed positions, profit = 0')
        return 0
      }
      
      const now = new Date()
      let cutoffDate = null
      
      if (days === 1) {
        cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      } else if (days === 7) {
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      } else if (days === 30) {
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      }

      const filtered = closedPositions.value.filter(pos => {
        if (!cutoffDate) return true
        
        const closedDateStr = pos.date_closed || pos.closedAt
        if (!closedDateStr) {
          console.log('[Dashboard] Position has no date_closed:', pos)
          return false
        }
        
        const closedDate = new Date(closedDateStr)
        if (isNaN(closedDate.getTime())) {
          console.log('[Dashboard] Invalid date:', closedDateStr)
          return false
        }
        
        const isInRange = closedDate >= cutoffDate && closedDate <= now
        return isInRange
      })

      console.log(`[Dashboard] Calculating profit for ${days ? days + ' days' : 'all time'}:`)
      console.log(`[Dashboard] Total closed positions: ${closedPositions.value.length}`)
      console.log(`[Dashboard] Filtered positions: ${filtered.length}`)
      
      const profit = filtered.reduce((sum, pos) => {
        const pnl = Number(pos.realized_pnl) || 0
        console.log(`[Dashboard] Position ${pos.position_id || pos.id}: PnL = ${pnl}, Date = ${pos.date_closed || pos.closedAt}`)
        return sum + pnl
      }, 0)

      console.log(`[Dashboard] Total profit: ${profit}`)
      return profit
    }

    // Load saved PnL index from localStorage
    const loadSavedPnlIndex = () => {
      try {
        const saved = localStorage.getItem('pnl_index')
        if (saved !== null) {
          const index = parseInt(saved, 10)
          if (!isNaN(index) && index >= 1 && index <= 15) {
            currentPnlIndex.value = index
          }
        }
      } catch (err) {
        console.error('Error loading saved PnL index:', err)
      }
    }

    // Save PnL index to localStorage
    const savePnlIndex = (index) => {
      try {
        localStorage.setItem('pnl_index', String(index))
      } catch (err) {
        console.error('Error saving PnL index:', err)
      }
    }

    // Update PnL index in background (but don't apply until next page load)
    const updatePnlIndexInBackground = () => {
      // Randomly move up or down by 1
      const direction = Math.random() < 0.5 ? -1 : 1
      let newIndex = currentPnlIndex.value + direction
      
      // Clamp between 1 and 15
      newIndex = Math.max(1, Math.min(15, newIndex))
      
      // Save to localStorage (will be used on next page load)
      savePnlIndex(newIndex)
      
      console.log(`[PnL] Background update: ${currentPnlIndex.value} -> ${newIndex} (will apply on next load)`)
    }

    // Start background PnL index updates
    const startPnlIndexUpdates = () => {
      // Clear existing interval if any
      if (pnlUpdateInterval) {
        clearInterval(pnlUpdateInterval)
      }
      
      // Random interval between 10-60 seconds
      const getRandomInterval = () => {
        return Math.floor(Math.random() * (60000 - 10000) + 10000) // 10-60 seconds in milliseconds
      }
      
      // Schedule first update
      const scheduleNextUpdate = () => {
        const interval = getRandomInterval()
        pnlUpdateInterval = setTimeout(() => {
          updatePnlIndexInBackground()
          scheduleNextUpdate() // Schedule next update
        }, interval)
      }
      
      scheduleNextUpdate()
    }

    // Stop background PnL index updates
    const stopPnlIndexUpdates = () => {
      if (pnlUpdateInterval) {
        clearTimeout(pnlUpdateInterval)
        pnlUpdateInterval = null
      }
    }

    // Load PnL data
    const loadPnl = async () => {
      try {
        const response = await apiService.getPnl()
        if (response.ok && response.data && response.data.data) {
          pnlData.value = response.data.data
          
          // Load saved index and start background updates
          loadSavedPnlIndex()
          startPnlIndexUpdates()
        }
      } catch (err) {
        console.error('Error loading PnL:', err)
      }
    }

    // Load positions
    const loadPositions = async () => {
      if (!userData.value.id) return

      try {
        const response = await apiService.getPositions(userData.value.id)
        if (response.ok && response.data) {
          openedPositions.value = response.data.opened_positions || []
          closedPositions.value = response.data.closed_positions || []
        }
      } catch (err) {
        console.error('Error loading positions:', err)
      }
    }

    const loadDashboardData = async () => {
      if (!userData.value.id) {
        error.value = t('dashboard.failedToGetUserId')
        return
      }

      isLoading.value = true
      error.value = null

      try {
        await Promise.all([
          loadPositions(),
          loadPnl()
        ])
        // PnL (live + custom + copytrade) обслуживает общий composable
        startPnl()
      } catch (err) {
        console.error('Error loading dashboard data:', err)
        if (err.message === 'TIMEOUT') {
          error.value = t('dashboard.requestTimeout')
        } else {
          error.value = t('dashboard.errorLoadingData')
        }
      } finally {
        isLoading.value = false
        // Wait for DOM to update and canvas to be rendered
        await nextTick()
        setTimeout(() => {
          drawChart()
        }, 100)
      }
    }

    // Load position negative streak from localStorage
    const loadPositionNegativeStreak = (positionId) => {
      try {
        const key = `position_negative_streak_${positionId}`
        const saved = localStorage.getItem(key)
        if (saved) {
          const data = JSON.parse(saved)
          return {
            count: Number(data.count) || 0,
            lastPnlPercent: data.lastPnlPercent || null,
            lastCheck: data.lastCheck || null
          }
        }
      } catch (e) {
        console.error('Error loading position negative streak:', e)
      }
      return { count: 0, lastPnlPercent: null, lastCheck: null }
    }

    // Save position negative streak to localStorage
    const savePositionNegativeStreak = (positionId, count, pnlPercent) => {
      try {
        const key = `position_negative_streak_${positionId}`
        const data = {
          count: count,
          lastPnlPercent: pnlPercent,
          lastCheck: new Date().toISOString()
        }
        localStorage.setItem(key, JSON.stringify(data))
      } catch (e) {
        console.error('Error saving position negative streak:', e)
      }
    }

    // Calculate PnL for open position with negative streak handling
    const getPositionPnlUsdt = (position, positionIndex) => {
      const positionId = position.position_id || position.id
      const margin = Number(position.margin) || 0

      // 1. Единый источник PnL (custom_pnl → live Binance → server) из общего composable.
      // То же значение, что и на вкладке Trade и в Balance Details.
      const shared = pnlById(positionId)
      if (shared) {
        return { usdt: Number(shared.usdt) || 0, percent: Number(shared.percent) || 0 }
      }

      // 2. Fallback: Обычный PnL из базы данных (pnl1-pnl15)
      if (!pnlData.value || !position.date_opened) {
        return null
      }

      try {
        // Each position gets its own unique PnL index based on position index
        // This ensures each position has a different PnL value
        // We use positionIndex to cycle through pnl1-pnl15, ensuring uniqueness
        const pnlIndex = ((positionIndex % 15) + 1)
        const pnlKey = `pnl${pnlIndex}`
        let pnlPercent = pnlData.value[pnlKey]

        if (pnlPercent == null) return null

        let pnlUsdt = (margin * pnlPercent) / 100

        // Track negative streak for this position
        if (positionId) {
          const streak = loadPositionNegativeStreak(positionId)
          
          // Only update streak if PnL percent has changed (new data from API)
          const pnlPercentChanged = streak.lastPnlPercent === null || 
                                   Math.abs(streak.lastPnlPercent - pnlPercent) > 0.01
          
          // Check if current PnL is negative
          if (pnlUsdt < 0) {
            // Only increment streak if PnL data has changed
            let newCount = streak.count
            if (pnlPercentChanged) {
              newCount = streak.count + 1
              savePositionNegativeStreak(positionId, newCount, pnlPercent)
            } else {
              // Use existing count if PnL hasn't changed
              newCount = streak.count
            }

            // If position has been negative 3-5 times in a row, gradually increase PnL
            if (newCount >= 3 && newCount <= 5) {
              // Gradually increase: add 0.5% per streak count (max 2.5% boost)
              const boostPercent = (newCount - 2) * 0.5 // 0.5% at 3, 1% at 4, 1.5% at 5
              const boostUsdt = (margin * boostPercent) / 100
              pnlUsdt += boostUsdt
              // Recalculate percent based on adjusted USDT
              pnlPercent = (pnlUsdt / margin) * 100
            } else if (newCount > 5) {
              // After 5 times, apply maximum boost of 2.5%
              const maxBoostPercent = 2.5
              const maxBoostUsdt = (margin * maxBoostPercent) / 100
              pnlUsdt += maxBoostUsdt
              // Recalculate percent based on adjusted USDT
              pnlPercent = (pnlUsdt / margin) * 100
            }
          } else {
            // PnL is positive or zero, reset streak
            if (streak.count > 0) {
              savePositionNegativeStreak(positionId, 0, pnlPercent)
            }
          }
        }

        return {
          usdt: pnlUsdt,
          percent: pnlPercent
        }
      } catch (e) {
        console.error('Error calculating PnL:', e)
        return null
      }
    }

    // Formatting functions
    const formatBalance = (balance) => {
      const num = Number(balance) || 0
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    const formatProfit = (profit) => {
      const num = Number(profit) || 0
      const sign = num >= 0 ? '+' : ''
      return `${sign}${num.toFixed(2)} USDT`
    }

    const formatROI = (roi) => {
      const num = Number(roi) || 0
      const sign = num >= 0 ? '+' : ''
      return `${sign}${num.toFixed(2)}%`
    }

    const formatMargin = (margin) => {
      if (margin == null) return '0.00'
      const num = Number(margin)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    const formatPrice = (price) => {
      if (price == null) return 'N/A'
      const num = Number(price)
      if (isNaN(num)) return 'N/A'
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    // Debug panel helper functions
    const getDebugSource = (position) => {
      const positionId = position.position_id || position.id
      
      // Check custom PnL first
      if (positionId && customPnls.value[positionId] != null) {
        return { type: 'custom', label: '📝 Custom PnL' }
      }
      
      // Check if using real prices from CoinGecko
      const coinId = position.coin_id
      if (coinId && realPrices.value[positionId]?.currentPrice) {
        return { type: 'coingecko', label: '🌐 CoinGecko API' }
      }
      
      // Fallback to database PnL
      return { type: 'database', label: '💾 Database (pnl1-15)' }
    }

    const getDebugEntryPrice = (position) => {
      const positionId = position.position_id || position.id
      
      // First check realPrices cache
      if (realPrices.value[positionId]?.entryPrice) {
        return '$' + realPrices.value[positionId].entryPrice.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6
        })
      }
      
      // Then check position entry_price
      if (position.entry_price) {
        return '$' + Number(position.entry_price).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6
        })
      }
      
      return '—'
    }

    const getDebugCurrentPrice = (position) => {
      const positionId = position.position_id || position.id
      
      if (realPrices.value[positionId]?.currentPrice) {
        return '$' + realPrices.value[positionId].currentPrice.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6
        })
      }
      
      return '—'
    }

    const formatDebugDate = (dateString) => {
      if (!dateString) return '—'
      try {
        const date = new Date(dateString)
        return date.toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return '—'
      }
    }

    const formatDebugTime = (timestamp) => {
      if (!timestamp) return '—'
      try {
        const date = new Date(timestamp)
        return date.toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch {
        return '—'
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return dateString
      }
    }

    const formatTradeDate = (dateString) => {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return dateString
      }
    }

    const formatVolume = (volume) => {
      if (volume == null) return 'N/A'
      const num = Number(volume)
      if (isNaN(num)) return 'N/A'
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    const formatTradePnL = (pnl) => {
      if (pnl == null) return 'N/A'
      const num = Number(pnl)
      if (isNaN(num)) return 'N/A'
      const sign = num >= 0 ? '+' : ''
      return `${sign}${num.toFixed(2)} USDT`
    }

    const formatPnlUsdt = (pnl) => {
      if (pnl == null) return 'N/A'
      
      if (typeof pnl === 'object' && pnl.usdt != null && pnl.percent != null) {
        const usdtNum = Number(pnl.usdt)
        const percentNum = Number(pnl.percent)
        if (isNaN(usdtNum) || isNaN(percentNum)) return 'N/A'
        // For positive values, add '+' sign; for negative, keep '-' sign
        const usdtSign = usdtNum >= 0 ? '+' : ''
        const percentSign = percentNum >= 0 ? '+' : ''
        return `${usdtSign}${usdtNum.toFixed(2)} USDT (${percentSign}${percentNum.toFixed(2)}%)`
      }
      
      const num = Number(pnl)
      if (isNaN(num)) return 'N/A'
      // For positive values, add '+' sign; for negative, keep '-' sign
      const sign = num >= 0 ? '+' : ''
      return `${sign}${num.toFixed(2)} USDT`
    }

    const getProfitClass = (value) => {
      const num = Number(value) || 0
      if (num > 0) return 'profit-positive'
      if (num < 0) return 'profit-negative'
      return ''
    }

    const getPnlClass = (pnl) => {
      if (pnl == null) return ''
      
      if (typeof pnl === 'object' && pnl.usdt != null) {
        const num = Number(pnl.usdt)
        if (isNaN(num)) return ''
        return num >= 0 ? 'pnl-positive' : 'pnl-negative'
      }
      
      const num = Number(pnl)
      if (isNaN(num)) return ''
      return num >= 0 ? 'pnl-positive' : 'pnl-negative'
    }

    // Format chart value for Y-axis
    const formatChartValue = (value) => {
      // Любые очень маленькие значения вокруг нуля отображаем как 0,
      // чтобы избежать -0, -1 и т.п. на плоском нулевом графике
      if (Math.abs(value) < 0.5) return '0'

      const absValue = Math.abs(value)
      if (absValue >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M'
      } else if (absValue >= 1000) {
        return (value / 1000).toFixed(1) + 'K'
      } else {
        return value.toFixed(0)
      }
    }

    // Format chart date for X-axis
    const formatChartDate = (date, period) => {
      if (period === 'today') {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      } else if (period === 'week' || period === 'month') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
      }
    }

    // Draw chart
    const drawChart = () => {
      if (!chartCanvas.value) {
        return
      }

      const canvas = chartCanvas.value
      const ctx = canvas.getContext('2d')
      
      // Ждём, пока canvas отрендерится и у него появится реальный размер
      const rect = canvas.getBoundingClientRect()
      if (!rect.width || !rect.height) {
        setTimeout(() => drawChart(), 100)
        return
      }

      const dpr = window.devicePixelRatio || 1
      const width = rect.width
      let height = rect.height

      // Фиксируем первую рассчитанную высоту графика и не даём ей бесконечно расти
      if (chartInitialHeight.value == null) {
        chartInitialHeight.value = height
      } else {
        height = Math.min(height, chartInitialHeight.value)
      }

      // Устанавливаем внутренний размер canvas с учётом devicePixelRatio
      // для чёткого рендеринга на Retina/HiDPI экранах
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'
      ctx.scale(dpr, dpr)

      // Clear canvas
      ctx.clearRect(0, 0, width, height)

      // Get chart data
      const originalChartData = getChartData()
      const hasData = originalChartData.length > 0
      
      // If no data, create a flat line at zero
      let chartData = originalChartData
      if (!hasData) {
        const now = new Date()
        const points = 20 // Number of points for the zero line
        
        chartData = []
        for (let i = 0; i < points; i++) {
          const date = new Date(now.getTime() - (points - 1 - i) * 60 * 60 * 1000)
          chartData.push({
            date: date,
            value: 0
          })
        }
      }
      
      currentChartData.value = chartData

      // Find min and max for scaling
      const values = chartData.map(d => d.value)
      const minValue = Math.min(...values, 0)
      const maxValue = Math.max(...values, 0)
      const range = maxValue - minValue || 1
      const isFlatZero = values.every(v => Math.abs(v) < 0.000001)

      // Padding with space for labels (only if we have data)
      const leftPadding = hasData ? 50 : 0
      const rightPadding = hasData ? 50 : 0
      const topPadding = hasData ? 20 : 0
      const bottomPadding = hasData ? 30 : 0
      const chartWidth = width - leftPadding - rightPadding
      const chartHeight = height - topPadding - bottomPadding

      // Draw grid lines and Y-axis labels (only if we have data)
      if (hasData) {
        ctx.strokeStyle = 'rgba(118, 223, 198, 0.1)'
        ctx.lineWidth = 1
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
        ctx.font = '11px sans-serif'
        ctx.textAlign = 'right'
        ctx.textBaseline = 'middle'

        const gridLines = 5
        for (let i = 0; i <= gridLines; i++) {
          const y = topPadding + (i / gridLines) * chartHeight

          // Draw grid line
          ctx.beginPath()
          ctx.moveTo(leftPadding, y)
          ctx.lineTo(width - rightPadding, y)
          ctx.stroke()

          // Draw Y-axis label on the left
          const labelText = isFlatZero
            ? '0'
            : formatChartValue(maxValue - (i / gridLines) * range)
          ctx.fillText(labelText, leftPadding - 10, y)

          // Draw Y-axis label on the right
          ctx.textAlign = 'left'
          ctx.fillText(labelText, width - rightPadding + 10, y)
          ctx.textAlign = 'right'
        }

        // Draw zero line
        if (minValue < 0 && maxValue > 0) {
          const zeroY = topPadding + chartHeight - ((0 - minValue) / range) * chartHeight
          ctx.strokeStyle = 'rgba(118, 223, 198, 0.3)'
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(leftPadding, zeroY)
          ctx.lineTo(width - rightPadding, zeroY)
          ctx.stroke()
        }

        // Draw X-axis labels (dates)
        ctx.textAlign = 'center'
        ctx.textBaseline = 'top'
        const xLabelsCount = Math.min(chartData.length, 5)
        for (let i = 0; i < xLabelsCount; i++) {
          const index = Math.floor((i / (xLabelsCount - 1)) * (chartData.length - 1))
          const point = chartData[index]
          const x = leftPadding + (index / (chartData.length - 1 || 1)) * chartWidth
          const date = new Date(point.date)
          const labelText = formatChartDate(date, selectedPeriod.value)
          ctx.fillText(labelText, x, height - bottomPadding + 5)
        }
      } else {
        // If no data, just draw a zero line in the middle
        const zeroY = height / 2
        ctx.strokeStyle = 'rgba(118, 223, 198, 0.3)'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(0, zeroY)
        ctx.lineTo(width, zeroY)
        ctx.stroke()
      }

      // Draw filled area under the line (only if we have data)
      if (hasData) {
        ctx.fillStyle = 'rgba(118, 223, 198, 0.1)'
        ctx.beginPath()
        ctx.moveTo(leftPadding, topPadding + chartHeight)

        chartData.forEach((point, index) => {
          const x = leftPadding + (index / (chartData.length - 1 || 1)) * chartWidth
          const y = topPadding + chartHeight - ((point.value - minValue) / range) * chartHeight

          if (index === 0) {
            ctx.lineTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        })

        ctx.lineTo(leftPadding + chartWidth, topPadding + chartHeight)
        ctx.closePath()
        ctx.fill()
      }

      // Draw line
      ctx.strokeStyle = '#76dfc6'
      ctx.lineWidth = 2
      ctx.beginPath()

      if (hasData) {
        chartData.forEach((point, index) => {
          const x = leftPadding + (index / (chartData.length - 1 || 1)) * chartWidth
          const y = topPadding + chartHeight - ((point.value - minValue) / range) * chartHeight

          if (index === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        })
      } else {
        // Draw horizontal line at zero
        ctx.moveTo(0, height / 2)
        ctx.lineTo(width, height / 2)
      }

      ctx.stroke()

      // Draw vertical line and point at hover position if tooltip is visible (only if we have data)
      if (hasData && tooltip.value.visible && tooltip.value.pointIndex !== undefined) {
        const pointIndex = tooltip.value.pointIndex
        const point = chartData[pointIndex]
        const x = leftPadding + (pointIndex / (chartData.length - 1 || 1)) * chartWidth
        const y = topPadding + chartHeight - ((point.value - minValue) / range) * chartHeight

        // Draw vertical line
        ctx.strokeStyle = 'rgba(118, 223, 198, 0.5)'
        ctx.lineWidth = 1
        ctx.setLineDash([5, 5])
        ctx.beginPath()
        ctx.moveTo(x, topPadding)
        ctx.lineTo(x, topPadding + chartHeight)
        ctx.stroke()
        ctx.setLineDash([])

        // Draw point
        ctx.fillStyle = '#76dfc6'
        ctx.beginPath()
        ctx.arc(x, y, 4, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // Handle chart hover
    const handleChartHover = (event) => {
      if (!chartCanvas.value || currentChartData.value.length === 0) return

      const canvas = chartCanvas.value
      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      updateTooltip(x, y)
    }

    // Handle chart touch
    const handleChartTouch = (event) => {
      if (!chartCanvas.value || currentChartData.value.length === 0) return
      
      // Don't show tooltip if we have no real data
      const originalData = getChartData()
      if (originalData.length === 0) return

      event.preventDefault()
      const canvas = chartCanvas.value
      const rect = canvas.getBoundingClientRect()
      const touch = event.touches[0] || event.changedTouches[0]
      const x = touch.clientX - rect.left
      const y = touch.clientY - rect.top

      updateTooltip(x, y)
    }

    // Update tooltip
    const updateTooltip = (x, y) => {
      if (!chartCanvas.value || currentChartData.value.length === 0) return
      
      // Don't show tooltip if we have no real data
      const originalData = getChartData()
      if (originalData.length === 0) return

      const canvas = chartCanvas.value
      const rect = canvas.getBoundingClientRect()
      const width = rect.width || 400
      const height = (chartInitialHeight.value || rect.height || 200)
      const leftPadding = 50
      const rightPadding = 50
      const topPadding = 20
      const bottomPadding = 30
      const chartWidth = width - leftPadding - rightPadding

      // Check if within chart bounds
      if (x < leftPadding || x > width - rightPadding || y < topPadding || y > height - bottomPadding) {
        hideTooltip()
        return
      }

      // Find closest point
      const relativeX = x - leftPadding
      const pointIndex = Math.round((relativeX / chartWidth) * (currentChartData.value.length - 1))
      const clampedIndex = Math.max(0, Math.min(pointIndex, currentChartData.value.length - 1))
      const point = currentChartData.value[clampedIndex]

      // Format date and value
      const date = new Date(point.date)
      const dateStr = formatTooltipDate(date, selectedPeriod.value)
      const valueStr = formatTooltipValue(point.value)

      // Update tooltip
      tooltip.value = {
        visible: true,
        x: x,
        y: y - 50, // Position above cursor
        date: dateStr,
        value: valueStr,
        pointIndex: clampedIndex
      }

      // Redraw chart to show hover line
      drawChart()
    }

    // Hide tooltip
    const hideTooltip = () => {
      tooltip.value.visible = false
      tooltip.value.pointIndex = undefined
      drawChart()
    }

    // Format tooltip date
    const formatTooltipDate = (date, period) => {
      if (period === 'today') {
        return date.toLocaleString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      } else if (period === 'week' || period === 'month') {
        return date.toLocaleString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      } else {
        return date.toLocaleString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric',
          hour: '2-digit', 
          minute: '2-digit' 
        })
      }
    }

    // Format tooltip value
    const formatTooltipValue = (value) => {
      const sign = value >= 0 ? '+' : ''
      return `${sign}${value.toFixed(2)} USDT`
    }

    // Get chart data
    const getChartData = () => {
      if (!closedPositions.value || closedPositions.value.length === 0) {
        // Если вообще нет закрытых позиций и выбран Today —
        // рисуем "пустой" нулевой график за последние 24 часа,
        // чтобы пользователь всё равно видел оси и мог взаимодействовать
        if (selectedPeriod.value === 'today') {
          const now = new Date()
          const cutoffDateToday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
          const pointsCountToday = 24
          const intervalMsToday = 60 * 60 * 1000

          const zeroData = []
          for (let i = 0; i < pointsCountToday; i++) {
            const pointDate = new Date(cutoffDateToday.getTime() + i * intervalMsToday)
            zeroData.push({
              date: pointDate,
              value: 0
            })
          }

          return zeroData
        }

        return []
      }

      const now = new Date()
      let cutoffDate = null
      let pointsCount = 30
      let intervalMs = 24 * 60 * 60 * 1000 // Default: 1 day

      switch (selectedPeriod.value) {
        case 'today':
          cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000) // Last 24 hours
          pointsCount = 24 // By hours
          intervalMs = 60 * 60 * 1000 // 1 hour
          break
        case 'week':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          pointsCount = 7 // By days
          intervalMs = 24 * 60 * 60 * 1000 // 1 day
          break
        case 'month':
          cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          pointsCount = 30 // By days
          intervalMs = 24 * 60 * 60 * 1000 // 1 day
          break
        case 'all-time': {
          // For all time, find the earliest position date
          const allDates = closedPositions.value
            .map(pos => {
              const dateStr = pos.date_closed || pos.closedAt
              return dateStr ? new Date(dateStr).getTime() : null
            })
            .filter(time => time !== null)
          
          if (allDates.length === 0) {
            return []
          }
          
          const earliestDate = new Date(Math.min(...allDates))
          const daysDiff = Math.ceil((now - earliestDate) / (24 * 60 * 60 * 1000))
          
          // Use reasonable number of points (max 100)
          pointsCount = Math.min(Math.max(daysDiff, 7), 100)
          cutoffDate = earliestDate
          intervalMs = (now - earliestDate) / pointsCount
          break
        }
      }

      // Filter closed positions by date
      const filteredPositions = closedPositions.value.filter(pos => {
        // Try both date_closed and closedAt fields
        const closedDateStr = pos.date_closed || pos.closedAt
        if (!closedDateStr) return false
        
        const closedDate = new Date(closedDateStr)
        if (isNaN(closedDate.getTime())) return false
        
        if (!cutoffDate) return true
        return closedDate >= cutoffDate && closedDate <= now
      })

      if (filteredPositions.length === 0) {
        // Аналогично: если за выбранный период Today нет сделок,
        // строим нулевой график за последние 24 часа
        if (selectedPeriod.value === 'today' && cutoffDate) {
          const data = []
          for (let i = 0; i < pointsCount; i++) {
            const pointDate = new Date(cutoffDate.getTime() + i * intervalMs)
            data.push({
              date: pointDate,
              value: 0
            })
          }

          return data
        }

        return []
      }

      // Create time buckets
      const data = []
      // For "today", start from cutoffDate (24 hours ago), not from earliest position
      const startDate = (selectedPeriod.value === 'today' && cutoffDate) 
        ? cutoffDate 
        : (cutoffDate || new Date(Math.min(...filteredPositions.map(p => {
            const dateStr = p.date_closed || p.closedAt
            return new Date(dateStr).getTime()
          }))))
      
      for (let i = 0; i < pointsCount; i++) {
        const pointDate = new Date(startDate.getTime() + i * intervalMs)
        const nextDate = i < pointsCount - 1 
          ? new Date(startDate.getTime() + (i + 1) * intervalMs)
          : now

        // Find positions closed in this period
        const periodProfit = filteredPositions
          .filter(pos => {
            const closedDateStr = pos.date_closed || pos.closedAt
            if (!closedDateStr) return false
            
            const closedDate = new Date(closedDateStr)
            if (isNaN(closedDate.getTime())) return false
            
            return closedDate >= pointDate && closedDate < nextDate
          })
          .reduce((sum, pos) => sum + (Number(pos.realized_pnl) || 0), 0)

        data.push({
          date: pointDate,
          value: periodProfit
        })
      }

      // Calculate cumulative total
      let cumulative = 0
      return data.map(point => {
        cumulative += point.value
        return {
          date: point.date,
          value: cumulative
        }
      })
    }

    // Redraw chart when period changes
    watch(selectedPeriod, () => {
      nextTick(() => {
        setTimeout(() => {
          drawChart()
        }, 100)
      })
    })

    // Redraw chart when data changes
    watch([openedPositions, closedPositions], () => {
      nextTick(() => {
        setTimeout(() => {
          drawChart()
        }, 100)
      })
    }, { deep: true })

    // Handle window resize
    const handleResize = () => {
      if (chartCanvas.value) {
        drawChart()
      }
    }

    onMounted(() => {
      window.addEventListener('resize', handleResize)
      
      // Предзагружаем PositionsHistoryScreen для быстрого открытия
      import('./PositionsHistoryScreen.vue').catch(() => null)
      
      if (userData.value.id) {
        loadDashboardData()
      } else {
        const checkUserData = setInterval(() => {
          if (userData.value.id) {
            clearInterval(checkUserData)
            loadDashboardData()
          }
        }, 100)
        
        setTimeout(() => {
          clearInterval(checkUserData)
          if (!userData.value.id) {
            error.value = t('dashboard.failedToGetUserData')
          }
        }, 5000)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      stopPnlIndexUpdates()
      stopPnl()
    })

    // Navigate to positions history
    const goToPositionsHistory = () => {
      router.push('/positions-history')
    }

    // Navigate to airdrop
    const goToAirdrop = () => {
      router.push('/airdrop')
    }

    // Show alert when clicking on hidden coin name
    const showCoinHiddenAlert = () => {
      alert(t('dashboard.coinHiddenAlert'))
    }

    // Клик по названию пары: для собственных сделок пользователя (source: 'self')
    // не показываем заглушку «скрытой пары» — она остаётся только для копи-сделок.
    const onCoinClick = (position) => {
      if (position && position.source === 'self') return
      showCoinHiddenAlert()
    }

    // Handle icon error - fallback to SVG icon
    const handleIconError = (event) => {
      // If image fails to load, replace with SVG icon
      const svgIcon = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="7" fill="#76dfc6" stroke="#76dfc6" stroke-width="1"/>
          <text x="8" y="11" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="#053a1f" text-anchor="middle">H</text>
        </svg>
      `
      event.target.style.display = 'none'
      const wrapper = event.target.parentElement
      if (wrapper && !wrapper.querySelector('.hyper-icon-fallback')) {
        wrapper.insertAdjacentHTML('afterbegin', svgIcon)
        const fallback = wrapper.querySelector('svg')
        if (fallback) {
          fallback.classList.add('hyper-icon-fallback')
        }
      }
    }

    return {
      selectedPeriod,
      periods,
      isLoading,
      error,
      currentBalance,
      profitDay,
      profitWeek,
      profitMonth,
      profitAllTime,
      currentPeriodProfit,
      unrealizedPnl,
      roi,
      openedPositions,
      closedPositions,
      userLeverage,
      chartCanvas,
      tooltip,
      handleChartHover,
      handleChartTouch,
      hideTooltip,
      goToPositionsHistory,
      goToAirdrop,
      showCoinHiddenAlert,
      onCoinClick,
      handleIconError,
      loadDashboardData,
      formatBalance,
      formatProfit,
      formatROI,
      formatMargin,
      formatPrice,
      formatDate,
      formatTradeDate,
      formatVolume,
      formatTradePnL,
      formatPnlUsdt,
      getProfitClass,
      getPnlClass,
      getPositionPnlUsdt,
      openPositionsCount,
      closedPositionsCount,
      winRate,
      averagePnL,
      totalVolume,
      showDebugPanel,
      realPrices,
      getDebugSource,
      getDebugEntryPrice,
      getDebugCurrentPrice,
      formatDebugDate,
      formatDebugTime
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: var(--spacing-lg);
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dashboard-content {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding-bottom: 100px;
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.dashboard-title {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  flex: 1;
  min-width: 0;
}

.airdrop-button {
  background: var(--color-accent) !important;
  border: none !important;
  box-shadow: 0 2px 12px rgba(118, 223, 198, 0.3), 0 0 20px rgba(118, 223, 198, 0.15);
  animation: airdrop-glow 3s ease-in-out infinite;
  font-weight: 600 !important;
  white-space: nowrap;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.airdrop-token-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.airdrop-button .hyper-icon-fallback {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

@keyframes airdrop-glow {
  0%, 100% {
    box-shadow: 0 2px 12px rgba(118, 223, 198, 0.3), 0 0 20px rgba(118, 223, 198, 0.15);
  }
  50% {
    box-shadow: 0 4px 20px rgba(118, 223, 198, 0.5), 0 0 30px rgba(118, 223, 198, 0.3);
  }
}

.airdrop-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(118, 223, 198, 0.5), 0 0 30px rgba(118, 223, 198, 0.3) !important;
}

.period-selector {
  display: flex;
  gap: var(--spacing-xs);
  background: var(--color-bg-secondary);
  padding: var(--spacing-xs);
  border-radius: var(--radius-md);
  overflow: hidden;
  min-width: 0; /* Позволяет flex-элементам сжиматься */
}

.period-button {
  flex: 1;
  min-width: 0; /* Позволяет кнопке сжиматься */
  padding: var(--spacing-sm) clamp(4px, 1vw, var(--spacing-md));
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: clamp(10px, 2.5vw, var(--font-size-sm)); /* Масштабируемый размер шрифта */
  font-weight: 600;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap; /* Предотвращает перенос текста */
  overflow: hidden;
  text-overflow: ellipsis; /* Обрезает текст если не влезает */
  text-align: center;
}

.period-button:hover {
  background: rgba(118, 223, 198, 0.1);
}

.period-button.active {
  background: var(--color-accent);
  color: var(--color-bg-primary);
}

/* Адаптивность для очень маленьких экранов */
@media (max-width: 360px) {
  .period-selector {
    gap: 2px;
    padding: 2px;
  }
  
  .period-button {
    padding: var(--spacing-xs) 4px;
  }
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-md);
  padding-top: var(--spacing-xl);
}

.loader-dots {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-accent);
  animation: pulse 1.4s ease-in-out infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.error-title {
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--color-text-primary);
}

.error-message {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.dashboard-sections {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

.stats-container {
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

/* Hero Section - главный баланс */
.stats-hero {
  padding-bottom: var(--spacing-lg);
  border-bottom: 1px solid rgba(118, 223, 198, 0.15);
}

.stats-balance {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.stats-balance-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 500;
}

.stats-balance-value {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--color-accent);
}

.balance-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--color-accent);
  color: var(--color-bg-primary);
  border-radius: var(--radius-full);
  font-size: var(--font-size-sm);
  font-weight: 700;
  flex-shrink: 0;
}

/* Общий грид для метрик под балансом */
.stats-metrics-grid {
  margin-top: var(--spacing-lg);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-md);
}

.stats-metric-card {
  padding: var(--spacing-md);
  background: rgba(118, 223, 198, 0.03);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.stats-metric-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-weight: 500;
}

.stats-metric-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--color-text-primary);
}

.stats-metric-value.profit-positive {
  color: #22c55e;
}

.stats-metric-value.profit-negative {
  color: #ef4444;
}

.stats-metric-subvalues {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stats-metric-subvalue {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
}

.stats-icon {
  font-size: var(--font-size-xs);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
}

.stats-icon.profit-positive {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.stats-icon.profit-negative {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.stats-key-value.profit-positive {
  color: #22c55e;
}

.stats-key-value.profit-positive .stats-icon {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.stats-key-value.profit-negative {
  color: #ef4444;
}

.stats-key-value.profit-negative .stats-icon {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.stats-secondary-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-weight: 500;
}

.stats-secondary-value {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}

.stats-secondary-value.profit-positive {
  color: #22c55e;
}

.stats-secondary-value.profit-negative {
  color: #ef4444;
}

.stats-secondary-divider {
  width: 1px;
  height: 30px;
  background: rgba(118, 223, 198, 0.1);
  flex-shrink: 0;
}

/* Positions Card - улучшенный дизайн */
.positions-card {
  grid-column: 1 / -1;
}

.positions-stats {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-sm);
}

.position-stat-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
}

.position-stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  flex-shrink: 0;
}

.position-stat-icon.open-icon {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.position-stat-icon.closed-icon {
  background: rgba(118, 223, 198, 0.15);
  color: var(--color-accent);
}

.position-stat-icon svg {
  width: 16px;
  height: 16px;
}

.position-stat-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.position-stat-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-weight: 500;
  line-height: 1.2;
}

.position-stat-value {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1.2;
}

.position-stat-divider {
  width: 1px;
  height: 40px;
  background: rgba(118, 223, 198, 0.2);
  flex-shrink: 0;
}

@media (max-width: 480px) {
  .stats-balance-value {
    font-size: var(--font-size-xl);
  }

  .stats-metrics-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-sm);
  }

  .positions-card {
    grid-column: 1;
  }

  .positions-stats {
    gap: var(--spacing-sm);
  }

  .position-stat-icon {
    width: 28px;
    height: 28px;
  }

  .position-stat-icon svg {
    width: 14px;
    height: 14px;
  }

  .position-stat-value {
    font-size: var(--font-size-base);
  }

  .position-stat-divider {
    height: 36px;
  }
}

.chart-card {
  padding: var(--spacing-md);
}

.chart-container {
  width: 100%;
  height: 200px; /* базовая высота для мобильных/узких экранов */
  position: relative;
  cursor: crosshair;
}

.chart-canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
}

.chart-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  transform: translateX(-50%);
  z-index: 1000;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.tooltip-date {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  margin-bottom: 4px;
}

.tooltip-value {
  color: #76dfc6;
  font-weight: 600;
  font-size: 13px;
}

.trades-card {
  padding: var(--spacing-md);
}

.empty-trades {
  text-align: center;
  padding: var(--spacing-lg);
}

.empty-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.trades-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.trade-item {
  padding: var(--spacing-sm);
  background: var(--color-bg-primary);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.trade-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
  flex-wrap: wrap;
}

.trade-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.trade-instrument {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}

.trade-type {
  font-size: var(--font-size-xs);
  font-weight: 600;
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  text-transform: uppercase;
}

.trade-type.long {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.trade-type.short {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.trade-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-xs);
}

.trade-volume {
  color: var(--color-text-secondary);
}

.trade-pnl {
  font-weight: 700;
}

.trade-pnl.profit-positive {
  color: #22c55e;
}

.trade-pnl.profit-negative {
  color: #ef4444;
}

.positions-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.positions-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.position-card {
  padding: var(--spacing-md);
}

.positions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
}

.positions-section .section-title {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}

.history-button {
  flex-shrink: 0;
}

.empty-positions {
  text-align: center;
  padding: var(--spacing-xl);
}

.empty-positions .empty-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.position-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-md);
}

.cross-margin-label {
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--color-text-secondary);
  padding: 4px 8px;
  background: var(--color-bg-secondary);
  border-radius: var(--radius-sm);
  white-space: nowrap;
}

.position-pair {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.position-coin,
.position-instrument {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}

.position-coin.clickable {
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease;
}

.position-coin.clickable:hover {
  opacity: 0.7;
}

.position-coin.clickable:active {
  opacity: 0.5;
}

.position-direction {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: var(--font-size-xs);
  font-weight: 600;
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.position-direction.long {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.position-direction.short {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.position-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
  padding-top: var(--spacing-sm);
  border-top: 1px solid rgba(118, 223, 198, 0.1);
}

.position-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-sm);
  gap: var(--spacing-md);
  padding: var(--spacing-xs) 0;
}

.position-detail-row.highlight-row {
  background: rgba(118, 223, 198, 0.05);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  margin: 0 calc(var(--spacing-sm) * -1);
}

.detail-label {
  color: var(--color-text-secondary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.detail-value {
  color: var(--color-text-primary);
  font-weight: 600;
  text-align: right;
  display: flex;
  align-items: center;
  gap: 4px;
}

.pnl-value {
  font-size: var(--font-size-sm);
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0.02em;
}

.pnl-icon {
  font-size: var(--font-size-xs);
  display: inline-block;
}

.pnl-value.pnl-positive {
  color: #22c55e;
}

.pnl-value.pnl-negative {
  color: #ef4444;
}

@media screen and (min-width: 924px) {
  .dashboard-container {
    height: 100vh;
    padding-top: var(--spacing-xl);
    padding-right: var(--spacing-xl);
    padding-bottom: var(--spacing-xl);
    padding-left: 132px; /* место под левое меню */
    align-items: stretch;
    overflow-y: auto; /* скроллим содержимое Dashboard внутри экрана */
  }

  .dashboard-content {
    max-width: none;
    margin: 0;
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    grid-auto-rows: min-content;
    gap: var(--spacing-lg);
    padding-bottom: 32px;
  }

  .dashboard-header {
    grid-column: 1 / 3;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .dashboard-title {
    margin: 0;
  }

  .airdrop-button {
    margin-left: auto;
  }

  .period-selector {
    grid-column: 1 / 3;
  }

  .dashboard-sections {
    grid-column: 1 / 3;
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    grid-auto-rows: min-content;
    gap: var(--spacing-lg);
  }

  /* Слева блок со статами (Total Balance + метрики) */
  .stats-container {
    grid-column: 1 / 2;
    margin-bottom: 0;
  }

  /* Справа от Total Balance — график (на той же строке) */
  .chart-card {
    grid-column: 2 / 3;
  }

  /* Блок с открытыми позициями на всю ширину под ними */
  .positions-section {
    grid-column: 1 / 3;
  }

  /* Список позиций в две колонки */
  .positions-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--spacing-md);
  }

  /* Выравниваем визуальную высоту блока Total Balance и графика */
  .stats-container,
  .chart-card {
    min-height: 260px;
  }

  /* На десктопе график заполняет всю высоту своей карточки */
  .chart-card {
    display: flex;
    flex-direction: column;
  }

  .chart-container {
    flex: 1;
    height: 100%;
  }
}

@media (max-width: 480px) {
  .dashboard-container {
    padding: var(--spacing-md);
  }


  .position-detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-value {
    text-align: left;
  }
}

/* Debug Panel Styles */
.debug-panel {
  margin-top: var(--spacing-sm);
  padding: var(--spacing-sm);
  background: rgba(255, 193, 7, 0.1);
  border: 1px dashed rgba(255, 193, 7, 0.5);
  border-radius: var(--radius-md);
  font-size: 11px;
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xs);
  padding-bottom: var(--spacing-xs);
  border-bottom: 1px solid rgba(255, 193, 7, 0.3);
}

.debug-title {
  font-weight: 700;
  color: #ffc107;
}

.debug-source {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.debug-source.custom {
  background: rgba(156, 39, 176, 0.2);
  color: #ba68c8;
}

.debug-source.coingecko {
  background: rgba(76, 175, 80, 0.2);
  color: #81c784;
}

.debug-source.database {
  background: rgba(33, 150, 243, 0.2);
  color: #64b5f6;
}

.debug-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.debug-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.debug-label {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.debug-value {
  color: rgba(255, 255, 255, 0.9);
  font-family: monospace;
  font-weight: 600;
}

.debug-source-hint {
  font-size: 9px;
  color: rgba(255, 193, 7, 0.7);
  font-weight: 400;
}
</style>
